import {createSelector, createSlice, PayloadAction} from '@reduxjs/toolkit';
import {RootState, AppThunk} from '../../../app/store';
import {UserModel} from "../../../models/User.model";
import {PharmacyModel} from "../../../models/Pharmacy.model";
import {selectedPharmacyAction, setSelectedPharmacy} from "../pharmacySlice";
import http from "../../../shared/utils/http";


export interface AuthState {
    auth?: boolean;
    user?: UserModel;
    selectPharmacist?: string
    pharmacistEmployeesLimit: number,
    selectPharmacy?: PharmacyModel
    pharmacies?: PharmacyModel[]
    employees?: any[]

}

const initialState: AuthState = {
    pharmacistEmployeesLimit: 0
};


export const authSlice = createSlice({
    name: 'auth',
    initialState,
    // The `reducers` field lets us define reducers and generate associated actions
    reducers: {
        setUser: (state, action: PayloadAction<UserModel>) => {
            state.user = action.payload;
        },
        resetUser: (state) => {
            state.user = undefined;
        },
        setPharmacies: (state, action: PayloadAction<PharmacyModel[]>) => {
            state.pharmacies = action.payload;
        },
        setEmployees: (state, action: PayloadAction<any[]>) => {
            state.employees = action.payload;
        },
        resetEmployees: (state) => {
            state.employees = undefined;
        },
        setPharmacistEmployeesLimit: (state, action: PayloadAction<number>) => {
            state.pharmacistEmployeesLimit = action.payload;
        },
        resetPharmacistEmployeesLimit: (state) => {
            state.pharmacistEmployeesLimit = 0;
        },
        addPharmacy: (state, action: PayloadAction<PharmacyModel>) => {
            state.pharmacies = [...(state.pharmacies || []), action.payload];
        },
        editPharmacy: (state, action: PayloadAction<PharmacyModel>) => {
            state.pharmacies = (state.pharmacies || []).map(ph => ph.idDocument === action.payload.idDocument? action.payload : ph);
        },
        resetPharmacies: (state) => {
            state.pharmacies = [];
        },
        setSelectedPharmacist: (state, action: PayloadAction<string>) => {
            state.selectPharmacist = action.payload
        },
        setAuth: (state, action: PayloadAction<boolean>) => {
            state.auth = action.payload;
        }
    },
});

export const {
    setSelectedPharmacist,
    setPharmacies,
    addPharmacy,
    editPharmacy,
    resetPharmacies,
    setEmployees,
    resetEmployees,
    setPharmacistEmployeesLimit,
    resetPharmacistEmployeesLimit,
    setUser,
    setAuth,
    resetUser,
} = authSlice.actions;



export const authAction =
    (res: {user: UserModel, employees: any[], count: number, pharmacies?: PharmacyModel[]}): AppThunk =>
        (dispatch) => {
            dispatch(setUser(res.user))
            if(res.user.type === 'pharmacist'){
                dispatch(setSelectedPharmacist(res.user.idDocument))
                dispatch(setPharmacistEmployeesLimit(res?.user?.employees || 0))
            }
            if(res.pharmacies){
                dispatch(setPharmacies(res.pharmacies))
                if(res.pharmacies?.length){
                    const pharmLocalStorage = localStorage.getItem('pharmacy')
                    const findLocalStorage = pharmLocalStorage && res.pharmacies.find(ele => ele.idDocument === pharmLocalStorage)
                    const findDefault = res.pharmacies.find(ele => ele.default)
                    dispatch(selectedPharmacyAction(findLocalStorage || findDefault || res.pharmacies[0]))
                }
            }
            if(res.employees){
                dispatch(setEmployees(res.employees))
            }

            dispatch(setAuth(true))
        };
export const loadEmployeesAction =
    (callback?: () => void): AppThunk =>  (dispatch, getState) => {
        const pharmacist = getState().auth.selectPharmacist;
        if(pharmacist){
            http.get(`pharmacists/${pharmacist}/employees`).then(r => {
                dispatch(setEmployees(r.employees))
                dispatch(setPharmacistEmployeesLimit(r.pharmacist.employees))
                callback && callback()
            })
        }
    };

export const selectUser = (state: RootState) => state.auth.user;
export const selectUserType = createSelector(selectUser, (user) => user?.type)


export const selectAuth = (state: RootState) => state.auth.auth;

export const selectPharmacies = (state: RootState) => state.auth.pharmacies;
export const selectSelectedPharmacist= (state: RootState) => state.auth.selectPharmacist;
export const selectPharmacistEmployees= (state: RootState) => state.auth.employees;
export const selectPharmacistEmployeesLimit= (state: RootState) => state.auth.pharmacistEmployeesLimit;
export const selectCountPharmacistEmployees= createSelector(selectPharmacistEmployees, (employees = []) => employees.map(ele => ele.id).reduce((acc, ele) => [...acc, ...(acc.includes(ele)? []: [ele])], []).length);
export const selectCanAddEmployee= createSelector(selectPharmacistEmployeesLimit, selectCountPharmacistEmployees, (tot, employees) => employees < tot);
export const selectTooManyEmployees= createSelector(selectPharmacistEmployeesLimit, selectCountPharmacistEmployees, (tot, employees) => employees > tot);



export default authSlice.reducer;
